import { useEffect, useRef } from 'react';

import { useNotification, NOTIFICATION } from '../components/NotificationSystem';
import {
    isBuildStale, isLocalSolverLost, isPageBusy, mayAutoReload, noteAutoReload,
} from '../core/pageHealth';

/**
 * Keep a long-lived tab on a build that still works.
 *
 * Three ways in, deliberately unequal — a reload nobody sees can be offered
 * freely, an interruption has to have earned itself:
 *
 *   hidden and idle      any newer build reloads the page. Invisible, so being
 *                        broad about it costs nothing and keeps tabs from
 *                        accumulating URLs that a deploy has taken away.
 *   visible             only when this page can no longer solve locally, which
 *                        is a thing the user is about to notice anyway.
 *   a chunk that 404s   whatever a stale page reached for and did not get. The
 *                        cheapest honest signal there is: something actually
 *                        broke, so say so instead of predicting it.
 */

const MSG_STALE = { fr: 'Cette page date d\'une version précédente. Rechargez pour continuer.', en: 'This page is from an older version. Reload to continue.', de: 'Diese Seite stammt aus einer älteren Version. Zum Fortfahren neu laden.', es: 'Esta página es de una versión anterior. Recarga para continuar.', pt: 'Esta página é de uma versão anterior. Recarregue para continuar.' };
const MSG_RELOAD = { fr: 'Recharger', en: 'Reload', de: 'Neu laden', es: 'Recargar', pt: 'Recarregar' };

/** Webpack's own wording when a hashed chunk is no longer served. */
const CHUNK_ERROR_RE = /Loading (CSS )?chunk .* failed/i;

const pick = (msg, lang) => msg[lang] || msg.en;

/** Toast with a reload button, kept until the user acts on it or closes it. */
function offerReload(showNotification, lang, msg) {
    showNotification(pick(msg, lang), NOTIFICATION.INFO, 0, {
        label: pick(MSG_RELOAD, lang),
        onClick: () => window.location.reload(),
    });
}

/** The local solver is gone from this page. Called by a run that found out. */
export function notifyStaleLocalSolve(showNotification, lang) {
    offerReload(showNotification, lang, MSG_STALE);
}

export function useStaleReload(lang) {
    const { showNotification } = useNotification();
    // One offer per page. A user who dismisses it has answered; a generation
    // that actually degrades will say so again on its own.
    const offeredRef = useRef(false);

    useEffect(() => {
        if (typeof document === 'undefined') return undefined;

        const offerOnce = (msg) => {
            if (offeredRef.current) return;
            offeredRef.current = true;
            offerReload(showNotification, lang, msg);
        };

        const check = async () => {
            if (document.hidden) {
                // Nobody is looking and nothing is running, so take the current
                // version now instead of asking for it later. The active build
                // is in localStorage and the page is in the URL, so the tab
                // comes back exactly as it was left.
                if (isPageBusy() || !mayAutoReload()) return;
                if (!await isBuildStale()) return;
                noteAutoReload();
                window.location.reload();
                return;
            }
            // Visible: interrupt only for something already lost. A newer build
            // on its own is not worth a word — most deploys leave this page
            // entirely functional.
            if (offeredRef.current) return;
            if (await isLocalSolverLost()) offerOnce(MSG_STALE);
        };

        // A dynamic import that 404s. Nothing else can repair this — the URL
        // came from the bundle that is already running — so it is the one case
        // worth interrupting a visible page for without being asked.
        const onRejection = (event) => {
            const err = event?.reason;
            if (String(err?.name || '') !== 'ChunkLoadError'
                && !CHUNK_ERROR_RE.test(String(err?.message || ''))) return;
            offerOnce(MSG_STALE);
        };

        // Returning to the tab is the moment a reload costs least and matters
        // most: whatever the user does next is what would have been degraded.
        document.addEventListener('visibilitychange', check);
        window.addEventListener('focus', check);
        window.addEventListener('unhandledrejection', onRejection);
        return () => {
            document.removeEventListener('visibilitychange', check);
            window.removeEventListener('focus', check);
            window.removeEventListener('unhandledrejection', onRejection);
        };
    }, [lang, showNotification]);
}

export default useStaleReload;
